import { useCallback, useEffect, useRef, useState } from "react";
import { ImageContent } from "../../../views/assistant/types";

/**
 * Supported image MIME types for VLM
 */
export const SUPPORTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
];

/** Split files into image/* files and everything else, preserving order. */
export function splitImageFiles(files: File[]): {
  images: File[];
  others: File[];
} {
  const images: File[] = [];
  const others: File[] = [];
  for (const file of files) {
    if (file.type.startsWith("image/")) {
      images.push(file);
    } else {
      others.push(file);
    }
  }
  return { images, others };
}

/**
 * Hand the images in `files` to `addImages`, or to `onVisionUnsupported` when
 * the model cannot take them. Returns the non-image files for document upload.
 */
export function routeImageFiles(
  files: File[],
  supportsVision: boolean,
  addImages: (images: File[]) => void,
  onVisionUnsupported: () => void,
): File[] {
  const { images, others } = splitImageFiles(files);
  if (images.length > 0) {
    if (supportsVision) {
      addImages(images);
    } else {
      onVisionUnsupported();
    }
  }
  return others;
}

/**
 * Maximum image size in bytes (10MB)
 */
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

/** Reason a file cannot be attached as an image, or null when it can. */
function imageProblem(file: File): string | null {
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    return `Unsupported image type: ${file.type}. Supported types: PNG, JPEG, GIF, WebP`;
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return `Image too large: ${(file.size / 1024 / 1024).toFixed(
      1,
    )}MB. Maximum size: 10MB`;
  }
  return null;
}

export interface PastedImage {
  id: string;
  /** Base64 encoded image data */
  data: string;
  /** MIME type */
  mediaType: string;
  /** Preview URL for display */
  previewUrl: string;
  /** Original file name if available */
  fileName?: string;
  /** File size in bytes */
  size: number;
}

interface UseImagePasteOptions {
  /** Maximum number of images allowed */
  maxImages?: number;
  /** Whether paste handling is enabled (e.g., false when model doesn't support vision) */
  enabled?: boolean;
  /** Custom error message to show when paste is disabled */
  disabledMessage?: string;
}

interface UseImagePasteReturn {
  /** Currently pasted images */
  images: PastedImage[];
  /** Add an image from a File object */
  addImage: (file: File) => Promise<void>;
  /** Add a batch of images; the cap and every rejection apply to the whole batch */
  addImages: (files: File[]) => Promise<void>;
  /** Remove an image by ID */
  removeImage: (id: string) => void;
  /** Clear all images */
  clearImages: () => void;
  /** Handle paste event - attach to input element */
  handlePaste: (event: React.ClipboardEvent) => void;
  /** Convert images to backend format */
  toImageContent: () => ImageContent[];
  /** Whether there are any images */
  hasImages: boolean;
  /** Error message if any */
  error: string | null;
  /** Images still being read; submit should wait until this is zero */
  pendingCount: number;
}

/**
 * Hook for chat image attachments sent to a VLM (Vision Language Model).
 * `handlePaste` takes images from the clipboard; `addImages` takes File
 * objects from a picker or drop. Validation failures surface via `error`.
 *
 * @example
 * ```tsx
 * const { images, handlePaste, addImages, removeImage, toImageContent } =
 *   useImagePaste({ maxImages: 5, enabled: supportsVision });
 *
 * // In your component
 * <TextField onPaste={handlePaste} />
 * // From a picker or drop: addImages(files)
 *
 * // When sending message
 * const messageContent = {
 *   text: inputText,
 *   images: toImageContent()
 * };
 * ```
 */
export function useImagePaste(
  options: UseImagePasteOptions = {},
): UseImagePasteReturn {
  const {
    maxImages = 10,
    enabled = true,
    disabledMessage = "This model does not support image inputs",
  } = options;

  const [images, setImagesState] = useState<PastedImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  // The ref is the synchronous source of truth and state mirrors it for
  // rendering. Batches read files asynchronously, so a count captured at
  // render time is stale by the time a batch commits.
  const imagesRef = useRef<PastedImage[]>([]);
  const setImages = useCallback(
    (update: (prev: PastedImage[]) => PastedImage[]) => {
      imagesRef.current = update(imagesRef.current);
      setImagesState(imagesRef.current);
    },
    [],
  );

  // Bumped by clearImages so a batch still reading when the user sends is
  // discarded instead of landing on the next message.
  const clearGenerationRef = useRef(0);

  // Clear error when enabled changes (e.g., switching to a vision-capable persona)
  useEffect(() => {
    if (enabled) {
      setError(null);
    }
  }, [enabled]);

  // Revoke preview URLs on unmount. The cleanup closure alone would only see
  // the empty initial list.
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((img) => {
        URL.revokeObjectURL(img.previewUrl);
      });
    };
  }, []);

  const generateId = useCallback(() => {
    return `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data:image/xxx;base64, prefix
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }, []);

  const addImages = useCallback(
    async (files: File[]) => {
      setError(null);

      const errors: string[] = [];
      const accepted: File[] = [];
      for (const file of files) {
        const problem = imageProblem(file);
        if (problem) {
          errors.push(problem);
        } else {
          accepted.push(file);
        }
      }

      // Trim against the current count so a large pick does not read files
      // that cannot fit. The check after reading is authoritative: another
      // batch may have committed meanwhile.
      const capMessage = `Maximum ${maxImages} images allowed`;
      const room = Math.max(0, maxImages - imagesRef.current.length);
      if (accepted.length > room) {
        errors.push(capMessage);
        accepted.splice(room);
      }

      const generation = clearGenerationRef.current;
      setPendingCount((n) => n + accepted.length);
      const added: PastedImage[] = [];
      try {
        for (const file of accepted) {
          try {
            const base64Data = await fileToBase64(file);
            added.push({
              id: generateId(),
              data: base64Data,
              mediaType: file.type,
              previewUrl: URL.createObjectURL(file),
              fileName: file.name || `pasted-image-${Date.now()}`,
              size: file.size,
            });
          } catch (err) {
            errors.push(
              file.name
                ? `Failed to process image "${file.name}"`
                : "Failed to process image",
            );
            console.error("Error processing image:", err);
          }
        }
      } finally {
        setPendingCount((n) => n - accepted.length);
      }

      if (generation !== clearGenerationRef.current) {
        added.forEach((img) => URL.revokeObjectURL(img.previewUrl));
        return;
      }

      const overflow = added.splice(
        Math.max(0, maxImages - imagesRef.current.length),
      );
      if (overflow.length > 0) {
        overflow.forEach((img) => URL.revokeObjectURL(img.previewUrl));
        errors.push(capMessage);
      }

      if (added.length > 0) {
        setImages((prev) => [...prev, ...added]);
      }
      if (errors.length > 0) {
        setError(Array.from(new Set(errors)).join(" "));
      }
    },
    [maxImages, fileToBase64, generateId, setImages],
  );

  const addImage = useCallback((file: File) => addImages([file]), [addImages]);

  const removeImage = useCallback(
    (id: string) => {
      setImages((prev) => {
        const imageToRemove = prev.find((img) => img.id === id);
        if (imageToRemove) {
          URL.revokeObjectURL(imageToRemove.previewUrl);
        }
        return prev.filter((img) => img.id !== id);
      });
      setError(null);
    },
    [setImages],
  );

  const clearImages = useCallback(() => {
    clearGenerationRef.current += 1;
    imagesRef.current.forEach((img) => {
      URL.revokeObjectURL(img.previewUrl);
    });
    setImages(() => []);
    setError(null);
  }, [setImages]);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      // Look for image items in clipboard
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          event.preventDefault(); // Prevent default paste behavior for images

          // If paste is disabled (e.g., model doesn't support vision), show error
          if (!enabled) {
            setError(disabledMessage);
            return;
          }

          const file = item.getAsFile();
          if (file) {
            addImage(file);
          }
          return; // Only handle one image per paste
        }
      }
    },
    [enabled, addImage, disabledMessage],
  );

  const toImageContent = useCallback((): ImageContent[] => {
    return images.map((img) => ({
      data: img.data,
      media_type: img.mediaType,
      detail: "auto" as const,
    }));
  }, [images]);

  return {
    images,
    addImage,
    addImages,
    removeImage,
    clearImages,
    handlePaste,
    toImageContent,
    hasImages: images.length > 0,
    error,
    pendingCount,
  };
}

export default useImagePaste;
